import { LoadingOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { css } from '@linaria/core';
import { Link } from '@tanstack/react-router';
import { Button, Flex, Grid, Layout, Spin } from 'antd';

import DataBoundary from '@components/data-boundary';
import { Account } from '@lib/types';

import { ROUTE_PATHS } from '@modules/route-paths';

import BrandLogo from '../brand-logo';
import CartButton from './components/cart-button';
import GlobalCardSearch from './components/global-card-search';
import OrderNotifications from './components/order-notifications';
import UserDropdown from './components/user-dropdown';
import renderSearchError from './render-search-error';
import { useAppTheme } from '../../config/theme/provider';

const styles = {
  header: css`
    position: relative;
    min-height: 64px;
    height: max-content;
    padding-inline: 0;
    border-bottom: 1px solid var(--ant-color-border-secondary);
    box-shadow: 0 4px 16px rgba(24, 24, 27, 0.04);
    background: var(--ant-color-bg-container);
    backdrop-filter: blur(16px);
  `,
  content: css`
    width: 100%;
    max-width: 1528px;
    min-height: 64px;
    margin-inline: auto;
    padding-inline: clamp(16px, 3vw, 32px);
    position: relative;
    z-index: 1;

    a {
      height: fit-content;
    }

    @media (max-width: 760px) {
      flex-wrap: wrap;
      padding-inline: clamp(8px, 2vw, 16px);
      padding-block: 8px;
    }
  `,
  actions: css`
    justify-self: end;
  `,
  mobileSearchBar: css`
    max-width: unset;
  `,
};

interface AppHeaderProps {
  account?: Account | null;
  accountLoading?: boolean;
}

const AppHeader = ({ account, accountLoading = false }: AppHeaderProps) => {
  const { mode, toggleTheme } = useAppTheme();

  const breakpoints = Grid.useBreakpoint();
  const accountVerified = Boolean(account?.verifiedAt);

  return (
    <Layout.Header className={styles.header}>
      <Flex
        className={styles.content}
        justify="space-between"
        align="center"
        gap={breakpoints.md ? 'large' : 'small'}
      >
        <BrandLogo />
        {breakpoints.md ? <GlobalCardSearch /> : null}
        <DataBoundary>
          <Flex
            className={styles.actions}
            gap={breakpoints.sm ? 12 : 6}
            align="center"
          >
            {account ? (
              <>
                {accountVerified ? (
                  <>
                    <CartButton accountId={account.id} />
                    <OrderNotifications />
                  </>
                ) : null}
                <UserDropdown hideName={!breakpoints.lg} />
              </>
            ) : accountLoading ? (
              <Spin indicator={<LoadingOutlined spin />} />
            ) : (
              <>
                <Button
                  icon={mode === 'light' ? <SunOutlined /> : <MoonOutlined />}
                  shape="circle"
                  onClick={toggleTheme}
                />
                <Link to={ROUTE_PATHS.LOGIN}>
                  <Button type="primary">Entrar</Button>
                </Link>
              </>
            )}
          </Flex>
        </DataBoundary>
        {!breakpoints.md ? (
          <DataBoundary
            loadingFallback={
              <Spin
                indicator={<LoadingOutlined spin />}
                size="small"
                aria-label="Carregando busca"
              />
            }
            errorFallback={renderSearchError}
          >
            <GlobalCardSearch className={styles.mobileSearchBar} />
          </DataBoundary>
        ) : null}
      </Flex>
    </Layout.Header>
  );
};

export default AppHeader;
